import { AlertWithChildren } from '@common/components/Alert/Alert';
import { Checkbox } from '@common/components/Checkbox/Checkbox';
import { useIsWindowWiderThan } from '@common/hooks';
import { useTranslations } from '@lang';
import { gridTheme } from '@utils/grid';
import { useFormContext } from 'react-hook-form';
import { getCancellationPolicy } from 'shared-utils';
import {
  AlertContainer,
  Border,
  CancellationPolicy,
  CheckboxContainer,
  CheckinCheckoutContainer,
  ItemDetails,
  ItemNameContainer,
  ItemPrice,
  MoreDetailsButton,
  OrderDetails,
  OrdersContainer,
  PackageDetails,
} from '../Account.styles';
import { getGuestSummaryString } from '../helpers/getGuestSummaryString';
import { MappedOrderItem } from '../helpers/mapOrderDetails.helper';
import { MobileMoreDetails } from './MobileMoreDetails';
import { CancelOrderFormValues } from './NewCancelOrderForm';
import { DropdownIndicator } from '@common/components/Menu/common/components/Item/Button/Button.styles';
import { useState } from 'react';
import { some } from '@utils/some';
import { OrderGroupHeading, SpaOrderGroupText } from './OrderSummary.styles';

type OrderSummaryProps = {
  orderItems: MappedOrderItem[];
};

export function OrderSummary({ orderItems }: OrderSummaryProps) {
  const { t } = useTranslations();
  const isDesktop = useIsWindowWiderThan(gridTheme.breakpoints.md);
  const { setValue, watch } = useFormContext<CancelOrderFormValues>();
  const selectedItems = watch('orderItemIds');
  const [expandedItems, setExpandedItems] = useState<number[]>([]);

  const handleExpand = (id) => {
    if (expandedItems.includes(id)) {
      setExpandedItems((arr) => arr.filter((i) => i !== id));
    } else {
      setExpandedItems((arr) => [...arr, id]);
    }
  };

  const renderPackage = (item: MappedOrderItem, isSelected: boolean, canBeCancelled: boolean) => {
    const isExpanded = expandedItems.includes(item.id);

    const children = orderItems.filter((i) => i.packageParentId === item.id) || [];
    const packageItems = [item, ...children];
    const childrenIds = children.map((child) => child.id);

    return (
      <>
        <PackageDetails
          isDesktop={isDesktop}
          isSelectable={canBeCancelled}
          isChecked={false}
          key={item.id}
        >
          <CheckboxContainer>
            <Checkbox
              id={String(item.id)}
              data-testid={`order-${item.id}-checkbox`}
              onChange={() => {
                setValue(
                  'orderItemIds',
                  isSelected
                    ? selectedItems.filter((i) => !childrenIds.includes(i) && i !== item.id)
                    : [...selectedItems, item.id, ...childrenIds],
                  { shouldDirty: true }
                );
              }}
              checked={isSelected}
            />
          </CheckboxContainer>
          <ItemNameContainer>{item.rate}</ItemNameContainer>
          <ItemPrice isDesktop={isDesktop}>{item.price}</ItemPrice>
          <MoreDetailsButton onClick={() => handleExpand(item.id)} type="button">
            <DropdownIndicator open={isExpanded} />
          </MoreDetailsButton>
        </PackageDetails>
        {isExpanded &&
          packageItems.map((packageItem) => (
            <OrderDetails
              isDesktop={isDesktop}
              isSelectable={canBeCancelled}
              isChecked={false}
              key={packageItem.id}
            >
              {renderOrderItemDetails(packageItem, false, false)}
            </OrderDetails>
          ))}
      </>
    );
  };

  const renderOrderItemDetails = (
    item: MappedOrderItem,
    showPrice: boolean,
    showRate: boolean,
    onlySelectableAsGroup?: boolean
  ) => {
    const isSelected = Array.isArray(selectedItems) && selectedItems.includes(item.id);

    return (
      <>
        {!onlySelectableAsGroup && (
          <CheckboxContainer>
            <Checkbox
              id={String(item.id)}
              data-testid={`order-${item.id}-checkbox`}
              onChange={() => {
                setValue(
                  'orderItemIds',
                  isSelected
                    ? selectedItems.filter((i) => i !== item.id)
                    : [...selectedItems, item.id],
                  { shouldDirty: true }
                );
              }}
              checked={isSelected}
              disabled={onlySelectableAsGroup}
            />
          </CheckboxContainer>
        )}
        <ItemNameContainer>{item.name}</ItemNameContainer>
        <CheckinCheckoutContainer>
          <span>{item.checkIn}</span>
          {item.checkOut ? <span> - {item.checkOut}</span> : <></>}
        </CheckinCheckoutContainer>
        {!isDesktop && showPrice && <ItemPrice isDesktop={isDesktop}>{item.price}</ItemPrice>}
        {isDesktop ? (
          <>
            <ItemDetails>
              <div>
                <span>{getGuestSummaryString(item.adults, item.children, item.guests)}</span>
                {item.rate && showRate ? <span>, {item.rate}</span> : <></>}
              </div>
              {item.extras.length ? (
                <span>
                  {t('treatment.extrasAndAdditions')}: {item.extras.join(', ')}
                </span>
              ) : (
                <></>
              )}
            </ItemDetails>

            {showPrice && <ItemPrice isDesktop={isDesktop}>{item.price}</ItemPrice>}
          </>
        ) : (
          <MobileMoreDetails item={item} />
        )}
      </>
    );
  };

  const renderOrderItems = (items: MappedOrderItem[], itemGroupType: 'spa' | 'rooms' | 'table') => {
    const groupItemIds = items.map((item) => item.id);
    const allItemsInGroupSelected = groupItemIds.every((id) => selectedItems.includes(id));
    return (
      <>
        <OrderGroupHeading>
          <Checkbox
            id={`${itemGroupType}-group-checkbox`}
            data-testid={`${itemGroupType}-group-checkbox`}
            onChange={() => {
              setValue(
                'orderItemIds',
                allItemsInGroupSelected
                  ? selectedItems.filter((i) => !groupItemIds.includes(i))
                  : [...selectedItems, ...groupItemIds],
                { shouldDirty: true }
              );
            }}
            checked={allItemsInGroupSelected}
          />
          {itemGroupType === 'spa' && t('basket.spa')}
          {itemGroupType === 'rooms' && t('basketDrawer.rooms')}
          {itemGroupType === 'table' && t('basketDrawer.dining')}
        </OrderGroupHeading>
        {itemGroupType === 'spa' && (
          <SpaOrderGroupText>
            <div>{t('cancelOrderForm.groupCancellation')}</div>
          </SpaOrderGroupText>
        )}
        {items.map((item) => {
          const { line1, line2, canBeCancelled } = getCancellationPolicy(item.cancellationPolicy);
          const isPackageParent = item.dynamicPackageItemType === 'parent';
          const isSelected = Array.isArray(selectedItems) && selectedItems.includes(item.id);

          return (
            <>
              <Border>
                {isPackageParent ? (
                  renderPackage(item, isSelected, canBeCancelled)
                ) : (
                  <>
                    <OrderDetails
                      isDesktop={isDesktop}
                      isSelectable={canBeCancelled && itemGroupType !== 'spa'}
                      isChecked={false}
                      key={item.id}
                    >
                      {renderOrderItemDetails(item, true, true, itemGroupType === 'spa')}
                    </OrderDetails>
                  </>
                )}
              </Border>
              {item.type === 'rooms' ? (
                <AlertContainer>
                  <AlertWithChildren noMaxWidth>
                    <CancellationPolicy>
                      <div>{line1}</div>
                      {line2 ? <div>{line2}</div> : <></>}
                    </CancellationPolicy>
                  </AlertWithChildren>
                </AlertContainer>
              ) : (
                <></>
              )}
            </>
          );
        })}
      </>
    );
  };

  const spaItems = orderItems.filter(
    (item) => item.type.includes('spa') && item.dynamicPackageItemType !== 'child'
  );
  const roomsItems = orderItems.filter((item) => item.type.includes('room'));
  const tablesItems = orderItems.filter(
    (item) => item.type.includes('table') && item.dynamicPackageItemType !== 'child'
  );

  return (
    <OrdersContainer>
      {some(roomsItems) && renderOrderItems(roomsItems, 'rooms')}
      {some(spaItems) && renderOrderItems(spaItems, 'spa')}
      {some(tablesItems) && renderOrderItems(tablesItems, 'table')}
    </OrdersContainer>
  );
}
